import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BatchSlotBarProps {
  total: number;
  remaining: number;
  showLabel?: boolean;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BatchSlotBar({
  total,
  remaining,
  showLabel = true,
  className,
}: BatchSlotBarProps) {
  const safeRemaining = Math.max(0, Math.min(remaining, total));
  const remainingPct  = total > 0 ? (safeRemaining / total) * 100 : 0;
  const bookedPct     = total > 0 ? ((total - safeRemaining) / total) * 100 : 100;
  const isFullyBooked = safeRemaining === 0;

  // Color is keyed on how many slots remain
  const urgency =
    isFullyBooked     ? "full"
    : remainingPct < 30  ? "critical"
    : remainingPct < 60  ? "warning"
    : "ok";

  const barColor: Record<typeof urgency, string> = {
    ok:       "bg-green-500 dark:bg-green-400",
    warning:  "bg-amber-500 dark:bg-amber-400",
    critical: "bg-red-500 dark:bg-red-400",
    full:     "bg-red-600 dark:bg-red-500",
  };

  const labelText =
    isFullyBooked
      ? "Fully booked"
      : `${safeRemaining} of ${total} slot${total !== 1 ? "s" : ""} available`;

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {/* Track */}
      <div
        className="relative h-1.5 rounded-full overflow-hidden bg-surface-overlay"
        role="progressbar"
        aria-valuenow={safeRemaining}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={labelText}
      >
        {/* Fill (booked slots — grows from the left) */}
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500 ease-out",
            barColor[urgency],
            urgency === "critical" && "animate-pulse",
          )}
          style={{ width: `${bookedPct}%` }}
        />
      </div>

      {/* Label */}
      {showLabel && (
        <p
          className={cn(
            "text-[11px] font-medium",
            isFullyBooked
              ? "text-red-600 dark:text-red-400"
              : urgency === "critical"
                ? "text-red-600 dark:text-red-400"
                : urgency === "warning"
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-content-tertiary",
          )}
        >
          {labelText}
        </p>
      )}
    </div>
  );
}

BatchSlotBar.displayName = "BatchSlotBar";
